<script>
import debounce from 'lodash.debounce'
import axios from 'axios'
export default {
  props: {
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    comboClass: {
      type: String,
      required: true,
      default: '',
    },
    fetchUrl: {
      type: String,
      default: '',
    },
    perPage: {
      type: Number,
      default: 10,
    },
    value: {
      type: String,
      default: '',
    },
    inputObject: {
      type: Object,
      default: null,
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    cache: {
      type: Boolean,
      default: false,
    },
    group: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      items: [],
      pagination: {
        total: 0,
        offset: 0,
      },
      cachedQueries: {},
      isLoading: false,
      search: null,
    }
  },
  computed: {
    formattedItems() {
      return this.items.map((entry) => {
        const Description =
          entry.Description.length > this.descriptionLimit
            ? entry.Description.slice(0, this.descriptionLimit) + '...'
            : entry.Description

        return Object.assign({}, entry, { Description })
      })
    },
    cachedItems: {
      get: function() {
        return this.$refs.combobox.cachedItems
      },
      set: function(newValue) {
        this.$refs.combobox.cachedItems = newValue
      },
    },
  },
  watch: {
    search: {
      handler(val) {
        val &&
          val !== (this.value ? this.value.value : '') &&
          this.debouncedSearch()
      },
    },
  },
  methods: {
    debouncedSearch: debounce(async function() {
      try {
        // Items have already been requested
        if (this.isLoading || !this.search) return

        // Since we use the cache-items prop filtering is performed in cachedItems array,
        // so if the query has been cached don't fetch from api
        if (this.cache && this.cachedQueries[this.search]) {
          return
        }
        this.cachedQueries[this.search] = true
        this.isLoading = true
        const {
          data: { results, count, offset },
          data: { ...groups },
        } = await axios.get(this.fetchUrl, {
          params: {
            q: this.search,
            ...(this.group ? { group: this.group } : {}),
            offset: '0',
            limit: this.perPage,
          },
        })

        if (this.grouped) {
          this.items = Object.keys(groups)
            .filter((el) => {
              return groups[el].length > 0
            })
            .reduce((acc, el, idx) => {
              acc.push({ header: el }) // group header
              acc.push(
                // group contents
                ...groups[el].map((obj) => {
                  obj.group = el
                  return obj
                })
              )
              acc.push({ divider: true }) // group divider
              return acc
            }, [])
        } else {
          this.items = results
          this.pagination.total = count
          this.pagination.offset = offset
        }
      } catch (error) {
        console.warn(error)
      } finally {
        this.isLoading = false
      }
    }, 500),
    async loadMore() {
      try {
        this.isLoading = true
        const {
          data: { results, count, offset },
        } = await axios.get(this.fetchUrl, {
          params: {
            q: this.search,
            ...(this.group ? { group: this.group } : {}),
            offset: this.pagination.offset + this.perPage,
            limit: this.perPage,
          },
        })
        this.items = this.items.concat(results)
        this.pagination.total = count
        this.pagination.offset = offset
      } catch (error) {
        console.warn(error)
      } finally {
        this.isLoading = false
      }
    },
    handleInput(input) {
      if (typeof input === 'string') {
        this.$emit('update:value', input)
      } else {
        this.$emit('update:value', input.label)
        this.$emit('update:inputObject', input)
      }
    },
    clear() {
      this.search = ''
      this.items = []
    },
  },
}
</script>

<template>
  <v-combobox
    ref="combobox"
    :class="comboClass"
    :attach="'.' + comboClass"
    :value="value"
    v-bind="$attrs"
    :items="items"
    :loading="isLoading"
    :search-input.sync="search"
    :label="label"
    :placeholder="placeholder"
    hide-no-data
    :cache-items="cache"
    :no-filter="!cache"
    item-value="id"
    item-text="label"
    v-on="$listeners"
    @input="handleInput"
    @click:clear="clear"
  >
    <template
      v-if="!grouped && pagination.total > pagination.offset + perPage"
      v-slot:append-item
    >
      <v-btn :loading="isLoading" class="mt-5" small block @click="loadMore"
        >Fetch More Items</v-btn
      >
    </template>
    <template v-if="cache" v-slot:prepend>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="primary" dark v-bind="attrs" v-on="on">
            mdi-database
          </v-icon>
        </template>
        <span>Items are cached</span>
      </v-tooltip>
    </template>
    <template v-if="grouped" v-slot:item="data">
      <v-list-item-content>
        <v-list-item-title>
          {{ data.item.label || data.item.value }}
        </v-list-item-title>
      </v-list-item-content>
    </template>
  </v-combobox>
</template>

<style lang="scss" module>
@import '@design';
</style>
